// ABOUTME: Hook for visibility module data, filters, and category/city/keyword drilldowns.
// ABOUTME: Uses tRPC queries with enabled flags for conditional fetching.

import { useState, useCallback, useMemo } from 'react'
import { subDays, format } from 'date-fns'
import { trpc } from '@/lib/trpc'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import type {
    VisibilityOverviewResponse,
    CategoryVisibility,
    CityVisibility,
    VisibilityCategoryGeoSnapshotResponse,
    VisibilityGeoSnapshotResponse,
    VisibilityDrilldownResponse,
    VisibilityFilters,
    KeywordSOV,
    CustomDateRange,
} from '@/types'

/**
 * Helper to create a default date range (last 30 days ending at the given date)
 * Visibility defaults to 30 days unlike Availability which defaults to 7 days
 */
function createDefaultDateRange(endDate: Date): CustomDateRange {
    const startDate = subDays(endDate, 30)
    return {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
    }
}

type ViewMode = 'overview' | 'category-drilldown' | 'city-drilldown'
type DrilldownSource = 'category' | 'city' | 'all-cities' | null

interface UseVisibilityReturn {
    // Data
    overview: VisibilityOverviewResponse | null
    categories: CategoryVisibility[]
    cities: CityVisibility[]
    categoryDrilldown: VisibilityDrilldownResponse | null
    cityDrilldown: VisibilityDrilldownResponse | null
    categoryGeoSnapshot: VisibilityCategoryGeoSnapshotResponse | null
    cityGeoSnapshot: VisibilityGeoSnapshotResponse | null
    keywordGeoSnapshot: VisibilityCategoryGeoSnapshotResponse | null

    // State
    isLoading: boolean
    isLoadingDrilldown: boolean
    isLoadingKeywordGeo: boolean
    error: string | null
    viewMode: ViewMode
    drilldownSource: DrilldownSource
    selectedCategory: CategoryVisibility | null
    drilldownCategory: CategoryVisibility | null
    selectedCity: CityVisibility | null
    selectedKeyword: KeywordSOV | null
    filters: VisibilityFilters
    workspaceId: string | null

    // Actions
    setFilters: (filters: Partial<VisibilityFilters>) => void
    selectCategory: (category: CategoryVisibility | null) => void
    openCategoryDrilldown: (category: CategoryVisibility) => void
    openAllCitiesDrilldown: () => void
    openCityDrilldown: (city: CityVisibility) => void
    selectKeyword: (keyword: KeywordSOV | null) => void
    closeDrilldown: () => void
    refresh: () => Promise<void>

    // Kept for backwards compat
    drilldownToCity: (city: CityVisibility) => void
    drilldownToCategory: (category: CategoryVisibility) => void
}

function buildVisibilityParams(workspaceId: string, filters?: VisibilityFilters) {
    return {
        workspaceId,
        platform: (filters?.platform && filters.platform !== 'all') ? filters.platform : undefined,
        dateRange: filters?.dateRange,
        category: (filters?.category && filters.category !== 'All Categories') ? filters.category : undefined,
        cityId: filters?.cityId,
    }
}

function isAllCategoryName(value: string | null | undefined): boolean {
    if (!value) return false
    const normalized = value.trim().toLowerCase()
    return normalized === 'all categories' || normalized === 'all'
}

function createSyntheticCategory(name: string): CategoryVisibility {
    return {
        id: name,
        name,
        overallSOV: 0,
        adSOV: 0,
        changeVsPrev: 0,
    }
}

export function useVisibility(): UseVisibilityReturn {
    const { currentWorkspace, loading: workspaceLoading } = useWorkspace()
    const workspaceId = currentWorkspace?.id ?? null

    // UI state
    const [viewMode, setViewMode] = useState<ViewMode>('overview')
    const [drilldownSource, setDrilldownSource] = useState<DrilldownSource>(null)
    const [drilldownCategory, setDrilldownCategory] = useState<CategoryVisibility | null>(null)
    const [selectedCity, setSelectedCity] = useState<CityVisibility | null>(null)
    const [selectedKeyword, setSelectedKeyword] = useState<KeywordSOV | null>(null)

    // Filters - start with default date range (last 30 days from today)
    const [filters, setFiltersState] = useState<VisibilityFilters>(() => ({
        platform: 'all',
        dateRange: createDefaultDateRange(new Date()),
        category: 'All Categories',
    }))

    const visibilityParams = useMemo(
        () => workspaceId ? buildVisibilityParams(workspaceId, filters) : null,
        [workspaceId, filters]
    )

    // --- Overview queries (run in parallel via React Query) ---
    const overviewQuery = trpc.visibility.overview.useQuery(
        visibilityParams!,
        { enabled: !!visibilityParams && !workspaceLoading }
    )

    const categoriesQuery = trpc.visibility.categories.useQuery(
        visibilityParams!,
        {
            enabled: !!visibilityParams && !workspaceLoading,
            select: (data) => (data as any)?.categories ?? data ?? [],
        }
    )

    // Cities always filtered by currently selected category (from filters)
    const citiesQuery = trpc.visibility.cities.useQuery(
        visibilityParams!,
        {
            enabled: !!visibilityParams && !workspaceLoading,
            select: (data) => (data as any)?.cities ?? data ?? [],
        }
    )

    const categories: CategoryVisibility[] = categoriesQuery.data ?? []

    const selectedCategory = useMemo(() => {
        const categoryName = filters.category?.trim()
        if (!categoryName || isAllCategoryName(categoryName)) {
            return null
        }
        return categories.find((category) => category.name === categoryName) ?? null
    }, [categories, filters.category])

    const selectedCategoryName = useMemo(() => {
        const categoryName = filters.category?.trim()
        return categoryName && categoryName.length > 0 ? categoryName : 'All Categories'
    }, [filters.category])

    const allCategoriesCard = useMemo(
        () => categories.find((category) => isAllCategoryName(category.name)) ?? null,
        [categories]
    )

    const activeCategoryDrilldownCategory = useMemo(() => {
        if (drilldownSource !== 'all-cities') return drilldownCategory

        if (!isAllCategoryName(selectedCategoryName)) {
            return categories.find((category) => category.name === selectedCategoryName)
                ?? createSyntheticCategory(selectedCategoryName)
        }

        return allCategoriesCard ?? createSyntheticCategory('All Categories')
    }, [allCategoriesCard, categories, drilldownCategory, drilldownSource, selectedCategoryName])

    const keywordGeoCityId = viewMode === 'city-drilldown' ? selectedCity?.id : undefined

    // --- Category drilldown queries (keyword groups + brand insights for selected category) ---
    const categoryDrilldownQuery = trpc.visibility.categoryDrilldown.useQuery(
        {
            categoryId: activeCategoryDrilldownCategory?.id ?? '',
            workspaceId: workspaceId!,
            platform: (filters.platform && filters.platform !== 'all') ? filters.platform : undefined,
            dateRange: filters.dateRange,
            cityId: drilldownSource === 'all-cities' ? undefined : filters.cityId,
        },
        { enabled: viewMode === 'category-drilldown' && !!activeCategoryDrilldownCategory && !!workspaceId }
    )

    const categoryGeoSnapshotQuery = trpc.visibility.categoryGeoSnapshot.useQuery(
        {
            categoryId: activeCategoryDrilldownCategory?.id ?? '',
            workspaceId: workspaceId!,
            platform: (filters.platform && filters.platform !== 'all') ? filters.platform : undefined,
            dateRange: filters.dateRange,
            cityId: drilldownSource === 'all-cities' ? undefined : filters.cityId,
        },
        { enabled: viewMode === 'category-drilldown' && !!activeCategoryDrilldownCategory && !!workspaceId }
    )

    // --- City drilldown queries ---
    const cityDrilldownQuery = trpc.visibility.cityDrilldown.useQuery(
        {
            cityId: selectedCity?.id ?? 0,
            workspaceId: workspaceId!,
            platform: (filters.platform && filters.platform !== 'all') ? filters.platform : undefined,
            dateRange: filters.dateRange,
            category: (filters.category && filters.category !== 'All Categories') ? filters.category : undefined,
        },
        { enabled: viewMode === 'city-drilldown' && !!selectedCity && !!workspaceId }
    )

    const cityGeoSnapshotQuery = trpc.visibility.cityGeoSnapshot.useQuery(
        {
            cityId: selectedCity?.id ?? 0,
            workspaceId: workspaceId!,
            platform: (filters.platform && filters.platform !== 'all') ? filters.platform : undefined,
            dateRange: filters.dateRange,
            category: (filters.category && filters.category !== 'All Categories') ? filters.category : undefined,
        },
        { enabled: viewMode === 'city-drilldown' && !!selectedCity && !!workspaceId }
    )

    // --- Keyword geo snapshot ---
    const keywordGeoSnapshotQuery = trpc.visibility.keywordGeoSnapshot.useQuery(
        {
            keywordId: selectedKeyword?.id ?? '',
            workspaceId: workspaceId!,
            platform: (filters.platform && filters.platform !== 'all') ? filters.platform : undefined,
            cityId: keywordGeoCityId,
        },
        { enabled: !!selectedKeyword && !!workspaceId }
    )

    // --- Actions ---
    const setFilters = useCallback((newFilters: Partial<VisibilityFilters>) => {
        if (Object.prototype.hasOwnProperty.call(newFilters, 'category') || Object.prototype.hasOwnProperty.call(newFilters, 'cityId')) {
            setSelectedKeyword(null)
        }
        setFiltersState(prev => ({ ...prev, ...newFilters }))
    }, [])

    // Selecting a category only updates the city-filter scope and does NOT open drilldown.
    const selectCategory = useCallback((category: CategoryVisibility | null) => {
        if (!category || isAllCategoryName(category.name)) {
            setFiltersState(prev => ({ ...prev, category: 'All Categories' }))
        } else {
            setFiltersState(prev => ({ ...prev, category: category.name }))
        }
        setSelectedKeyword(null)
    }, [])

    const openCategoryDrilldown = useCallback((category: CategoryVisibility) => {
        setDrilldownSource('category')
        setDrilldownCategory(category)
        setSelectedCity(null)
        setSelectedKeyword(null)
        setViewMode('category-drilldown')
    }, [])

    const openAllCitiesDrilldown = useCallback(() => {
        const resolvedCategory = !isAllCategoryName(selectedCategoryName)
            ? categories.find((category) => category.name === selectedCategoryName)
                ?? createSyntheticCategory(selectedCategoryName)
            : allCategoriesCard
                ?? createSyntheticCategory('All Categories')

        setDrilldownSource('all-cities')
        setDrilldownCategory(resolvedCategory)
        setSelectedCity(null)
        setSelectedKeyword(null)
        setViewMode('category-drilldown')
    }, [allCategoriesCard, categories, selectedCategoryName])

    const openCityDrilldown = useCallback((city: CityVisibility) => {
        setDrilldownSource('city')
        setSelectedCity(city)
        setDrilldownCategory(null)
        setSelectedKeyword(null)
        setViewMode('city-drilldown')
    }, [])

    // Kept for backwards compatibility.
    const drilldownToCategory = useCallback((category: CategoryVisibility) => {
        openCategoryDrilldown(category)
    }, [openCategoryDrilldown])

    // Kept for backwards compatibility.
    const drilldownToCity = useCallback((city: CityVisibility) => {
        openCityDrilldown(city)
    }, [openCityDrilldown])

    const selectKeyword = useCallback((keyword: KeywordSOV | null) => {
        setSelectedKeyword(keyword)
    }, [])

    const closeDrilldown = useCallback(() => {
        setViewMode('overview')
        setDrilldownSource(null)
        setDrilldownCategory(null)
        setSelectedCity(null)
        setSelectedKeyword(null)
    }, [])

    const utils = trpc.useUtils()
    const refresh = useCallback(async () => {
        if (!workspaceId) return
        await Promise.all([
            utils.visibility.overview.invalidate(),
            utils.visibility.categories.invalidate(),
            utils.visibility.cities.invalidate(),
            ...(viewMode === 'category-drilldown' && activeCategoryDrilldownCategory ? [
                utils.visibility.categoryDrilldown.invalidate(),
                utils.visibility.categoryGeoSnapshot.invalidate(),
            ] : []),
            ...(viewMode === 'city-drilldown' && selectedCity ? [
                utils.visibility.cityDrilldown.invalidate(),
                utils.visibility.cityGeoSnapshot.invalidate(),
            ] : []),
            ...(selectedKeyword ? [utils.visibility.keywordGeoSnapshot.invalidate()] : []),
        ])
    }, [workspaceId, utils, viewMode, activeCategoryDrilldownCategory, selectedCity, selectedKeyword])

    const isLoadingDrilldown = (
        (viewMode === 'category-drilldown' && (categoryDrilldownQuery.isLoading || categoryGeoSnapshotQuery.isLoading)) ||
        (viewMode === 'city-drilldown' && (cityDrilldownQuery.isLoading || cityGeoSnapshotQuery.isLoading))
    )

    return {
        // Data
        overview: overviewQuery.data ?? null,
        categories,
        cities: citiesQuery.data ?? [],
        categoryDrilldown: (viewMode === 'category-drilldown' ? categoryDrilldownQuery.data : null) ?? null,
        cityDrilldown: (viewMode === 'city-drilldown' ? cityDrilldownQuery.data : null) ?? null,
        categoryGeoSnapshot: (viewMode === 'category-drilldown' ? categoryGeoSnapshotQuery.data : null) ?? null,
        cityGeoSnapshot: (viewMode === 'city-drilldown' ? cityGeoSnapshotQuery.data : null) ?? null,
        keywordGeoSnapshot: selectedKeyword ? (keywordGeoSnapshotQuery.data ?? null) : null,

        // State
        isLoading: overviewQuery.isLoading || workspaceLoading,
        isLoadingDrilldown,
        isLoadingKeywordGeo: keywordGeoSnapshotQuery.isLoading,
        error: !workspaceId && !workspaceLoading ? 'No workspace selected' : overviewQuery.error?.message ?? null,
        viewMode,
        drilldownSource,
        selectedCategory,
        drilldownCategory: activeCategoryDrilldownCategory,
        selectedCity,
        selectedKeyword,
        filters,
        workspaceId,

        // Actions
        setFilters,
        selectCategory,
        openCategoryDrilldown,
        openAllCitiesDrilldown,
        openCityDrilldown,
        drilldownToCategory,
        drilldownToCity,
        selectKeyword,
        closeDrilldown,
        refresh,
    }
}
